import { Inject, Injectable } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  of, Subject
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap, takeUntil,
  tap
} from 'rxjs/operators';

import { AppErrorHandler } from '@sondermind/error-handling';
import { SondermindHttpService } from '@sondermind/http-deprecated';
import { AUTH_SERVICE, IAuthService } from '@sondermind/utilities/models-auth';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';

// zendesk
// eslint-disable-next-line no-var
declare var outbound: any;

// the response from exchanging an invite_code for a auth token
interface InviteExchangeData {
  user_id: number;
  token: string;
}

/**
 * The monolith's `/sessions/signup_strategy.json` response determines which signup handoff the
 * member-portal launcher uses for an invite code. `legacy_wizard` covers both the flag-off case and
 * any value this build does not recognize -- callers must fall back to the existing wizard for any
 * strategy other than `signup`, not just for an explicit `legacy_wizard`.
 */
export type SignupStrategy = 'signup' | 'legacy_wizard';

export type SignupStrategyResponse = {
  strategy: SignupStrategy;
  // Only present when strategy is 'signup' -- used as the Auth0 loginWithRedirect login_hint.
  email?: string;
};

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  constructor(
    private http: SondermindHttpService,
    @Inject(AUTH_SERVICE)
    private authSvc: IAuthService,
    private errorHandler: AppErrorHandler
  ) {
    this.user$.subscribe((user) => this.errorHandler.handleUserChange(user?.id?.toString()));
  }

  private userSubject = new BehaviorSubject<ProviderUser>(new ProviderUser());
  private destroyed$ = new Subject<void>();

  user$ = this.userSubject.asObservable();
  loggedIn$ = this.user$.pipe(map((u) => u && u.id !== 0));

  get user(): ProviderUser {
    return this.userSubject.getValue();
  }

  get loggedIn(): boolean {
    return this.user && this.user.id !== 0;
  }

  get canSubmitRequests(): boolean {
    return this.authSvc.isAuthenticated();
  }

  // check for a current token/user
  // set initial states, etc.
  initialize(): Observable<void> {
    // after initialization is complete, watch successful oauth authorizations,
    // reload the current user, and call successful login handlers
    this.authSvc.initialized$.awaiter().pipe(
      switchMap(() => this.authSvc.authorized$),
      switchMap(() => this.loadCurrentUser()),
      tap((u) => this.onSuccessfulLogin(u)),
      takeUntil(this.destroyed$),
    ).subscribe();

    // after initialization is complete, watch failed oauth authorizations,
    // set the user to null, to catch erroring out requests
    this.authSvc.initialized$.awaiter().pipe(
      switchMap(() => this.authSvc.authFlowComplete$),
      filter((auth) => !auth),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      if (!this.authSvc.isAuthenticated()) {
        this.userSubject.next(new ProviderUser());
      }
    });

    // when oauth is destroyed (logout), pipe the nullish user into the user subject
    this.authSvc.authDestroyed$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.userSubject.next(new ProviderUser());
    });

    return this.authSvc.initialize().pipe(
      switchMap((auth) => (auth ? this.loadCurrentUser() : of(new ProviderUser()))),
      tap((user) => {
        // set the user even if it's null
        // this way we can skip(1) on currentUser$ and get null if the user isn't logged in
        this.userSubject.next(user);
      }),
      map(() => undefined)
    );
  }

  // tear down the authentication service
  destroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // kick off the oauth flow
  login(returnTo: string = null): void {
    this.authSvc.login(returnTo);
  }

  // remove all authentication tokens, and reset the user in the auth service
  logout(): Observable<void> {
    return this.authSvc.logout();
  }

  loadCurrentUser(): Observable<ProviderUser> {
    return this.http.get<any>('/users/get_current_user.json', this.http.authRequestOptions())
      .pipe(
        map((res) => {
          const data = res.body;
          const user = new ProviderUser().load_from_json(data.user);
          return user;
        }),
        tap((u) => this.userSubject.next(u))
      );
  }

  reloadCurrentUser(): Observable<void> {
    const obs = this.loadCurrentUser();
    obs.subscribe(); // make hot

    return obs.pipe(map(() => undefined));
  }

  // if token is an auth service token, use that to log in directly. If not,
  // exchange the invite code for an auth token, then use that to log in
  loginWithInviteCode(userToken: string, isAuthSvcToken: boolean): Observable<ProviderUser> {
    return this.getAuthToken$(userToken, isAuthSvcToken).pipe(
      switchMap((token) => this.authSvc.loginWithAuthenticationToken(token)),
      switchMap(() => this.user$),
      shareReplay({ refCount: true })
    );
  }

  getAuthToken$(userToken: string, isAuthSvcToken: boolean): Observable<string> {
    if (isAuthSvcToken) {
      return of(userToken);
    }

    return this.exchangeInviteCode(userToken).pipe(map((d) => d.token));
  }

  // after a user has logged in successfully,
  private onSuccessfulLogin(user: ProviderUser): void {
    if (this.http.isImpersonating) { return }

    const uid = user.id.toString();

    if (typeof outbound !== 'undefined') {
      outbound.identify(uid, {});
    }
  }

  // we're not in a flow, and have no tokens, but maybe we can get a token from a server session?
  // the customHashFragment stuff is built into the silent-redirect.html
  silentRefresh(): Observable<ProviderUser> {
    return this.authSvc.silentRefresh().pipe(switchMap(() => this.user$));
  }

  /**
   * Asks the monolith which signup handoff an invite code should use. Unauthenticated, side-effect
   * free -- mirrors `exchangeInviteCode`'s request shape. Callers must treat a rejection the same as
   * an unrecognized strategy (fall back to the legacy wizard); this method does not swallow errors
   * itself so a caller can distinguish "no strategy" from "lookup failed" if it needs to.
   */
  signupStrategy(invite_code: string): Observable<SignupStrategyResponse> {
    const options = this.http.noAuthRequestOptions();
    return this.http
      .get(`/sessions/signup_strategy.json?invite_code=${invite_code}`, options)
      .pipe(
        map((res) => res.body as SignupStrategyResponse),
        catchError((res) => this.http.handleError(res)),
      );
  }

  // exchange the invite code for an authentication token
  // returns that token so we can send it to the oauth endpoint for authentication
  private exchangeInviteCode(invite_code: string): Observable<InviteExchangeData> {
    const options = this.http.noAuthRequestOptions();
    return this.http
      .post('/sessions/exchange_invite.json', { invite_code }, options)
      .pipe(
        map((res) => res.body as { token: InviteExchangeData; }),
        map((data) => data.token),
        catchError((res) => this.http.handleError(res)),
      );
  }
}
